import { Link } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronDown, Menu, X, Mail, MapPin, ArrowRight, 
  Wind, Heart, Brain, Leaf, Star, ChevronRight
} from 'lucide-react';

/* eslint-disable react/no-unescaped-entities */

// =========================================================
// HOMEPAGE V2 — Theme "Airy & Clean"
// Light, breathable, wellness-focused design
// =========================================================

const C = {
  // Backgrounds
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  softCyan: '#ECFEFF',
  
  // Brand
  sky: '#0EA5E9',
  skyLight: '#E0F2FE',
  skyDark: '#0284C7',
  cyan: '#06B6D4',
  cyanLight: '#CFFAFE',
  
  // Text
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

// Floating cloud/blob shapes for background
const FloatingBlob = ({ className, color, delay = 0 }) => (
  <div
    className={`absolute rounded-full blur-3xl opacity-40 ${className}`}
    style={{
      background: color,
      animation: `breathe 8s ease-in-out ${delay}s infinite`,
    }}
  />
);

const HomePagePreviewV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 200);
  };

  const menuItems = [
    { path: '/', label: 'Accueil', type: 'link' },
    {
      label: 'Troubles respiratoires', type: 'dropdown',
      items: [
        { path: '/hyperventilation', label: 'Hyperventilation' },
        { path: '/respiration-dysfonctionnelle', label: 'Respiration Dysfonctionnelle' },
        { path: '/asthme', label: 'Asthme' },
        { path: '/covid-long', label: 'COVID Long' },
      ],
    },
    {
      label: 'Ressources', type: 'dropdown',
      items: [
        { path: '/causes-troubles-respiratoires', label: 'Causes des Troubles' },
        { path: '/mecanique-respiratoire', label: 'Mécanique Respiratoire' },
        { path: '/respiration-optimale', label: 'Respiration Optimale' },
        { path: '/nerf-vague', label: 'Nerf Vague' },
      ],
    },
    {
      label: 'Techniques', type: 'dropdown',
      items: [
        { path: '/coherence-cardiaque', label: 'Cohérence Cardiaque' },
        { path: '/methode-buteyko', label: 'Méthode Buteyko' },
        { path: '/mobilite-mouvement', label: 'Mobilité et Mouvement' },
      ],
    },
    { path: '/evaluer-respiration', label: 'Tester', type: 'link' },
    { path: '/documents', label: 'Documents', type: 'link' },
  ];

  const features = [
    {
      icon: Wind,
      title: "Rééducation Respiratoire",
      description: "Techniques scientifiquement prouvées pour retrouver une respiration naturelle et apaisée."
    },
    {
      icon: Heart,
      title: "Cohérence Cardiaque",
      description: "Synchronisez votre cœur et votre souffle pour un équilibre nerveux optimal."
    },
    {
      icon: Brain,
      title: "Système Nerveux",
      description: "Comprenez et réglez votre système nerveux autonome grâce à la respiration."
    },
    {
      icon: Leaf,
      title: "Approche Naturelle",
      description: "Des méthodes douces, sans médicaments, pour améliorer votre qualité de vie."
    }
  ];

  const troubles = [
    { name: "Hyperventilation", path: "/hyperventilation", description: "Respiration excessive et ses conséquences" },
    { name: "Asthme", path: "/asthme", description: "Gestion naturelle des symptômes asthmatiques" },
    { name: "COVID Long", path: "/covid-long", description: "Rééducation post-COVID et récupération" },
    { name: "Respiration Dysfonctionnelle", path: "/respiration-dysfonctionnelle", description: "Patterns respiratoires inadaptés" },
  ];

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ 
        backgroundColor: C.white, 
        color: C.ink, 
        fontFamily: 'Figtree, sans-serif' 
      }}
      data-testid="homepage-v2"
    >
      {/* Global Styles for breathing animation */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
        
        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>

      {/* ============ HEADER - Crystal Glass ============ */}
      <header
        className="sticky top-0 z-50"
        style={{
          backgroundColor: 'rgba(255,255,255,0.7)',
          backdropFilter: 'saturate(180%) blur(24px)',
          WebkitBackdropFilter: 'saturate(180%) blur(24px)',
          borderBottom: '1px solid rgba(255,255,255,0.4)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex justify-between items-center py-4">
            {/* Logo & Name */}
            <div className="flex items-center gap-4">
              <button onClick={() => setProfileModalOpen(true)} className="group relative">
                <img
                  src="/julien-jaccard.png"
                  alt="Julien Jaccard"
                  className="w-12 h-12 rounded-full object-cover transition-all group-hover:ring-4 group-hover:ring-sky-100"
                  style={{ border: `2px solid ${C.sky}` }}
                />
              </button>
              <div className="hidden sm:block">
                <p className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif', color: C.ink }}>
                  Julien Jaccard
                </p>
                <p className="text-xs" style={{ color: C.muted }}>Physiothérapeute</p>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/7xvgj131_ChatGPT%20Image%2014%20juin%202026%2C%2008_42_5432.PNG"
                alt="Les Mains Nuages"
                className="hidden md:block w-10 h-10 object-contain ml-2"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {menuItems.map((item, index) => (
                item.type === 'link' ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-sm font-medium transition-colors hover:text-sky-600"
                    style={{ color: location.pathname === item.path ? C.sky : C.slate }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div key={item.label} className="relative" onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave}>
                    <button 
                      className="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-sky-600"
                      style={{ color: C.slate }}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform duration-300 ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="absolute top-full left-0 pt-4 w-64 z-50">
                        <div
                          className="rounded-2xl py-3 overflow-hidden"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(226,232,240,0.8)',
                            boxShadow: '0 20px 50px -15px rgba(0,0,0,0.1)',
                          }}
                        >
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setDropdownOpen(null)}
                              className="block px-5 py-2.5 text-sm transition-all hover:bg-sky-50 hover:text-sky-700"
                              style={{ color: C.slate }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ))}
              <button
                onClick={() => navigate('/app-landing')}
                className="px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                Exercices
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ color: C.ink }}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-6 border-t border-slate-100">
              <div className="flex flex-col gap-2">
                {menuItems.map((item, index) => (
                  item.type === 'link' ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 rounded-xl text-sm font-medium hover:bg-sky-50"
                      style={{ color: C.slate }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div key={item.label}>
                      <button
                        onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                        className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium hover:bg-sky-50"
                        style={{ color: C.slate }}
                      >
                        {item.label}
                        <ChevronDown size={16} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                      </button>
                      {dropdownOpen === index && (
                        <div className="pl-6 mt-1 space-y-1">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-4 py-2.5 rounded-xl text-sm hover:bg-sky-50"
                              style={{ color: C.muted }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ))}
                <div className="px-4 mt-4">
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/app-landing'); }}
                    className="w-full px-6 py-3 rounded-full text-sm font-medium"
                    style={{ backgroundColor: C.sky, color: C.white }}
                  >
                    Accéder aux exercices
                  </button>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-[100]"
          style={{ backgroundColor: 'rgba(15,23,42,0.3)', backdropFilter: 'blur(8px)' }}
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="rounded-3xl max-w-md w-full p-10 relative"
            style={{ backgroundColor: C.white, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setProfileModalOpen(false)} 
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 transition-colors"
              style={{ color: C.muted }}
            >
              <X size={20} />
            </button>
            <div className="flex flex-col items-center text-center">
              <img 
                src="/julien-jaccard.png" 
                alt="Julien Jaccard" 
                className="w-32 h-32 rounded-full object-cover mb-6" 
                style={{ border: `3px solid ${C.sky}` }} 
              />
              <h2 className="text-2xl font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif', color: C.ink }}>
                Julien Jaccard
              </h2>
              <p className="text-base mb-1" style={{ color: C.slate }}>Physiothérapeute</p>
              <div className="flex items-center gap-2 mb-8" style={{ color: C.muted }}>
                <MapPin size={16} />
                <span className="text-sm">Valais, Suisse</span>
              </div>
              <a
                href="mailto:julienjaccard@mains-nuages.ch"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium transition-all hover:shadow-lg"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                <Mail size={18} />
                julienjaccard@mains-nuages.ch
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============ HERO SECTION ============ */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {/* Background blobs */}
        <FloatingBlob className="w-[600px] h-[600px] -top-40 -right-40" color={C.cyanLight} delay={0} />
        <FloatingBlob className="w-[500px] h-[500px] top-1/2 -left-60" color={C.skyLight} delay={2} />
        <FloatingBlob className="w-[400px] h-[400px] bottom-0 right-1/4" color={C.softCyan} delay={4} />

        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text Content */}
            <div>
              <p 
                className="text-sm uppercase tracking-[0.2em] mb-6"
                style={{ color: C.sky, fontFamily: 'Figtree, sans-serif', fontWeight: 500 }}
              >
                Physiothérapie respiratoire
              </p>
              
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-8"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
              >
                Retrouvez une respiration{' '}
                <span style={{ color: C.sky, fontWeight: 400 }}>naturelle</span>{' '}
                et apaisée
              </h1>
              
              <p 
                className="text-lg lg:text-xl leading-relaxed mb-10 max-w-xl"
                style={{ color: C.slate }}
              >
                Hyperventilation, asthme, COVID long ou respiration dysfonctionnelle ? 
                Découvrez des techniques scientifiquement prouvées pour retrouver l'équilibre 
                et améliorer votre qualité de vie.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/app-landing')}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  style={{ backgroundColor: C.sky, color: C.white }}
                  data-testid="hero-cta-primary"
                >
                  Commencer les exercices
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate('/evaluer-respiration')}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  style={{ backgroundColor: C.skyLight, color: C.skyDark }}
                  data-testid="hero-cta-secondary"
                >
                  Tester ma respiration
                </button>
              </div>
            </div>

            {/* Right: Logo & Visual */}
            <div className="relative flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_03019b9c-e38d-4235-95ae-da304b949bfe/artifacts/7xvgj131_ChatGPT%20Image%2014%20juin%202026%2C%2008_42_5432.PNG" 
                alt="Physiothérapie Les Mains Nuages"
                className="w-96 h-96 lg:w-[480px] lg:h-[480px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES SECTION ============ */}
      <section className="py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p 
              className="text-sm uppercase tracking-[0.2em] mb-4"
              style={{ color: C.sky, fontWeight: 500 }}
            >
              Approche
            </p>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl mb-6"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
            >
              Une approche <span style={{ color: C.sky, fontWeight: 400 }}>globale</span> et naturelle
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: C.slate }}>
              Des méthodes douces et scientifiquement validées pour rééduquer votre respiration 
              et rétablir l'équilibre de votre système nerveux.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 rounded-3xl transition-all duration-500 hover:shadow-[0_8px_30px_rgba(14,165,233,0.08)] hover:-translate-y-1"
                style={{ 
                  backgroundColor: C.white,
                  border: `1px solid ${C.cloud}`
                }}
                data-testid={`feature-card-${index}`}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: C.skyLight }}
                >
                  <feature.icon size={24} strokeWidth={1.5} style={{ color: C.sky }} />
                </div>
                <h3 
                  className="text-xl mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
                >
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT SECTION ============ */}
      <section className="py-24 relative overflow-hidden">
        <FloatingBlob className="w-[400px] h-[400px] top-20 -right-40" color={C.cyanLight} delay={1} />
        
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-2">
              <div 
                className="relative rounded-3xl overflow-hidden shadow-2xl"
                style={{ aspectRatio: '4/5' }}
              >
                <img 
                  src="/julien-jaccard.png" 
                  alt="Julien Jaccard" 
                  className="w-full h-full object-cover"
                />
                <div 
                  className="absolute bottom-0 left-0 right-0 p-6"
                  style={{ 
                    background: 'linear-gradient(to top, rgba(15,23,42,0.8) 0%, transparent 100%)'
                  }}
                >
                  <p className="text-white text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Julien Jaccard
                  </p>
                  <p className="text-white/70 text-sm">Physiothérapeute depuis 1999</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              <p 
                className="text-sm uppercase tracking-[0.2em] mb-4"
                style={{ color: C.sky, fontWeight: 500 }}
              >
                À propos
              </p>
              <h2 
                className="text-3xl sm:text-4xl mb-8"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
              >
                Plus de 25 ans d'<span style={{ color: C.sky, fontWeight: 400 }}>expérience</span> 
                {' '}au service de votre bien-être
              </h2>
              
              <div className="space-y-6" style={{ color: C.slate }}>
                <p className="text-lg leading-relaxed">
                  Physiothérapeute depuis 1999, je me suis passionné pour les troubles respiratoires 
                  depuis plus de 5 ans. J'ai étudié, pratiqué sur moi-même toutes les techniques 
                  respiratoires, et développé une compréhension profonde du fonctionnement de cette 
                  machine organique que j'appelle <em>"Moi"</em>.
                </p>
                <p className="text-lg leading-relaxed">
                  Mon approche combine les dernières recherches scientifiques avec des techniques 
                  éprouvées comme la méthode Buteyko, la cohérence cardiaque, et la stimulation 
                  du nerf vague.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-10">
                <a
                  href="mailto:julienjaccard@mains-nuages.ch"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  style={{ backgroundColor: C.sky, color: C.white }}
                >
                  <Mail size={18} />
                  Me contacter
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TROUBLES SECTION ============ */}
      <section className="py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p 
              className="text-sm uppercase tracking-[0.2em] mb-4"
              style={{ color: C.sky, fontWeight: 500 }}
            >
              Troubles traités
            </p>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl mb-6"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
            >
              Je peux vous <span style={{ color: C.sky, fontWeight: 400 }}>aider</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {troubles.map((trouble, index) => (
              <Link
                key={index}
                to={trouble.path}
                className="group p-8 rounded-3xl flex items-center justify-between transition-all duration-500 hover:shadow-[0_8px_30px_rgba(14,165,233,0.12)] hover:-translate-y-1"
                style={{ 
                  backgroundColor: C.white,
                  border: `1px solid ${C.cloud}`
                }}
                data-testid={`trouble-card-${index}`}
              >
                <div>
                  <h3 
                    className="text-xl mb-2 group-hover:text-sky-600 transition-colors"
                    style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
                  >
                    {trouble.name}
                  </h3>
                  <p className="text-sm" style={{ color: C.slate }}>
                    {trouble.description}
                  </p>
                </div>
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ml-6 transition-all duration-300 group-hover:bg-sky-500"
                  style={{ backgroundColor: C.skyLight }}
                >
                  <ChevronRight 
                    size={20} 
                    className="transition-colors group-hover:text-white"
                    style={{ color: C.sky }} 
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="py-24 relative overflow-hidden">
        <FloatingBlob className="w-[500px] h-[500px] top-0 left-1/4" color={C.cyanLight} delay={0} />
        <FloatingBlob className="w-[400px] h-[400px] bottom-0 right-1/4" color={C.skyLight} delay={3} />

        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative z-10">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl mb-6"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
          >
            Prêt à <span style={{ color: C.sky, fontWeight: 400 }}>respirer</span> librement ?
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: C.slate }}>
            Commencez dès maintenant avec nos exercices guidés de respiration. 
            Cohérence cardiaque, méthode Buteyko, et bien plus encore.
          </p>
          <button
            onClick={() => navigate('/app-landing')}
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-medium transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            style={{ backgroundColor: C.sky, color: C.white }}
            data-testid="cta-exercises"
          >
            Accéder aux exercices
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p 
              className="text-sm uppercase tracking-[0.2em] mb-4"
              style={{ color: C.sky, fontWeight: 500 }}
            >
              Témoignages
            </p>
            <h2 
              className="text-3xl sm:text-4xl"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
            >
              Ce qu'ils en <span style={{ color: C.sky, fontWeight: 400 }}>disent</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((_, index) => (
              <div
                key={index}
                className="p-8 rounded-3xl"
                style={{ 
                  backgroundColor: C.white,
                  border: `1px solid ${C.cloud}`
                }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill={C.sky} style={{ color: C.sky }} />
                  ))}
                </div>
                <p className="text-base leading-relaxed mb-6" style={{ color: C.slate }}>
                  "Les exercices de respiration ont transformé ma vie quotidienne. 
                  Je gère beaucoup mieux mon stress et mon sommeil s'est amélioré."
                </p>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                    style={{ backgroundColor: C.skyLight, color: C.sky }}
                  >
                    {['M', 'S', 'L'][index]}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: C.ink }}>
                      {['Marie D.', 'Sophie L.', 'Laurent M.'][index]}
                    </p>
                    <p className="text-xs" style={{ color: C.muted }}>Patient(e)</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-16" style={{ backgroundColor: C.white, borderTop: `1px solid ${C.cloud}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <p 
                className="text-xl mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
              >
                Julien Jaccard
              </p>
              <p className="text-sm" style={{ color: C.muted }}>
                Physiothérapie · Rééducation respiratoire
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 text-sm" style={{ color: C.slate }}>
              <a 
                href="mailto:julienjaccard@mains-nuages.ch" 
                className="hover:text-sky-600 transition-colors"
                style={{ color: C.sky }}
              >
                julienjaccard@mains-nuages.ch
              </a>
              <Link to="/contact" className="hover:text-sky-600 transition-colors">Contact</Link>
              <Link to="/documents" className="hover:text-sky-600 transition-colors">Documents</Link>
            </div>
            
            <p className="text-xs" style={{ color: C.muted }}>
              © {new Date().getFullYear()} Les Mains Nuages
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePagePreviewV2;
